import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#labelAccountButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#accountObserver?.disconnect();
    this.#accountObserver = null;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  /** @type {MutationObserver | null} */
  #accountObserver = null;

  /**
   * YC a11y (A11Y-02): Shopify's hosted `<shopify-account>` hardcodes
   * `aria-label="Account"` on its signed-out button, while the theme slots the
   * visible "Log in" label into that same button. WCAG 2.5.3 needs the
   * accessible name to contain the visible text, and the component exposes no
   * attribute for it, so the slotted text is copied onto the button and
   * re-applied whenever the component re-renders. No-op when the header shows
   * the icon only (no slotted text) or once the customer is signed in.
   */
  #labelAccountButton = async () => {
    const account = this.querySelector('shopify-account');
    const text = this.querySelector('.account-button__text')?.textContent?.trim();
    if (!account || !text) return;

    try {
      await customElements.whenDefined('shopify-account');
    } catch {
      return;
    }

    const apply = () => {
      const button = account.shadowRoot?.querySelector('button[part="signed-out-avatar"]');
      if (button && button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
    };

    // The shadow root is attached on upgrade; give it a few frames in case the
    // component renders after `whenDefined` resolves.
    for (let attempt = 0; attempt < 10 && !account.shadowRoot; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (!account.shadowRoot || !this.isConnected) return;

    apply();
    // Only child-list changes are observed, so our own `setAttribute` never re-triggers it.
    this.#accountObserver = new MutationObserver(apply);
    this.#accountObserver.observe(account.shadowRoot, { childList: true, subtree: true });
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
